import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  FaPhone,
  FaPlay,
  FaPlus,
  FaUsers,
  FaCheckCircle,
  FaClock,
  FaSpinner,
  FaChartLine,
  FaFilter,
  FaFileUpload,
  FaUser,
  FaMapMarkerAlt,
  FaDollarSign,
  FaStickyNote,
} from "react-icons/fa";

import { batchCallsAPI, leadsAPI } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import "../styles/BatchCalling.css";
import { FaSync } from "react-icons/fa";

const LEADS_PAGE_SIZE = 20;


const BatchCalling = () => {
  const [batchCalls, setBatchCalls] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [uploadedFileNames, setUploadedFileNames] = useState([]); // ⬅️ NEW

  // NEW: leads pagination state
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsPagination, setLeadsPagination] = useState({
  next: null,
  previous: null,
  count: 0,
});
  // Upload preview modal
  const [uploadPreview, setUploadPreview] = useState(null);
  const [showUploadPreviewModal, setShowUploadPreviewModal] = useState(false);

  const handleUploadDocuments = async (formData) => {
  try {
    setUploading(true);
    const res = await leadsAPI.uploadDocuments(formData);

    setUploadPreview(res.data);
    setShowUploadPreviewModal(true);

    toast.success("Documents processed successfully!");
  } catch (err) {
    toast.error("Upload failed");
    console.error(err);
  } finally {
    setUploading(false);
    setUploadedFileNames([]); // ⬅️ clear once processing is done
  }
};

  // Create batch modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [batchName, setBatchName] = useState("");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [scheduledTime, setScheduledTime] = useState("");
  const [creating, setCreating] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const changeLeadsPage = (newPage) => {
  if (leadsLoading) return; // ignore spam clicks while loading

  const totalPages = Math.max(
    1,
    Math.ceil((leadsPagination.count || 0) / LEADS_PAGE_SIZE)
  );

  if (newPage < 1 || newPage > totalPages) return;
  setLeadsPage(newPage);
};
// inside your component (you can put this handler above the return)
//   const openDateTimePicker = (e) => {
//   if (e.target.showPicker) {
//     e.target.showPicker(); // Chrome / modern browsers
//   }
// };

const fetchData = useCallback(async () => {
  try {
    setLoading(true);
    setLeadsLoading(true);

    const params = filterStatus !== "all" ? { status: filterStatus } : {};

    const leadsParams = {
      page: leadsPage,
      page_size: LEADS_PAGE_SIZE,
    };

    const [batchResponse, statsResponse, leadsResponse] = await Promise.all([
      batchCallsAPI.getBatchCalls(params),
      batchCallsAPI.getBatchStats(),
      leadsAPI.getLeadsPaginated(leadsParams),
    ]);

    setBatchCalls(batchResponse.data.results ?? []);
    setStats(statsResponse.data);

    const leadsData = leadsResponse.data;
    setLeads(leadsData.results ?? []);
    setLeadsPagination({
      next: leadsData.next,
      previous: leadsData.previous,
      count: leadsData.count,
    });
  } catch (err) {
    console.error(err);
    toast.error("Failed to load batch calls");
  } finally {
    setLoading(false);      // whole-page initial loading
    setLeadsLoading(false); // only leads list in modal
  }
}, [filterStatus, leadsPage]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 1000000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Lead selection logic
  const toggleLeadSelection = (id) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllLeads = () => {
    setSelectedLeads(
      selectedLeads.length === leads.length ? [] : leads.map((l) => l.id)
    );
  };

  // Create batch call
  const handleCreateBatchCall = async () => {
    if (!batchName.trim()) return toast.error("Batch name is required");
    if (selectedLeads.length === 0)
      return toast.error("Select at least one lead");

    setCreating(true);
    try {
      const payload = { name: batchName, lead_ids: selectedLeads };
      if (scheduledTime)
        payload.scheduled_time = new Date(scheduledTime).toISOString();

      await batchCallsAPI.createBatchCall(payload);

      toast.success("Batch created!");
      setShowCreateModal(false);
      setBatchName("");
      setSelectedLeads([]);
      setScheduledTime("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Batch creation failed");
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (s) =>
    s ? new Date(s).toLocaleString() : "N/A";

  if (loading && batchCalls.length === 0) {
    return <LoadingSpinner text="Loading batch calls..." />;
  }


//   const handleDateMouseDown = (e) => {
//   // Only run for primary button clicks
//   if (e.button !== 0) return;

//   if (e.target.showPicker) {
//     // Stop the normal focus behavior and open the native picker
//     e.preventDefault();
//     e.target.showPicker();
//   }
// };

// Sync one batch
  const handleSyncBatch = async (id) => {
  try {
    toast.info("Syncing batch...");
    await batchCallsAPI.syncBatchCall(id);
    toast.success("Batch synced!");
    fetchData();
  } catch (err) {
    console.error(err);
    toast.error("Failed to sync batch");
  }
};

// Sync all batches
  const handleSyncAll = async () => {
  try {
    toast.info("Syncing all active batches...");
    await batchCallsAPI.syncAllBatchCalls();
    toast.success("All batches synced!");
    fetchData();
  } catch (err) {
    console.error(err);
    toast.error("Failed to sync all batches");
  }
};


  return (
    <div className="batch-calling-container">

      {/* HEADER */}
      <div className="page-header">
        <div className="header-left">
          <h1><FaPhone className="page-icon" /> Batch Calling</h1>
          <p>Schedule and automate calls to multiple recipients</p>
        </div>

        <div className="header-actions">

        <label
  className={`upload-doc-btn ${uploading ? "disabled" : ""}`}
>
  <FaFileUpload className="icon" />
  {uploading ? "Processing..." : "Upload Documents"}

  <input
    type="file"
    hidden
    multiple
    accept=".pdf,.doc,.docx"
    disabled={uploading}
    onChange={(e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      // ⬅️ store file names for the loader UI
      setUploadedFileNames(files.map((f) => f.name));

      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));

      handleUploadDocuments(formData);

      // allow selecting the same files again later
      e.target.value = "";
    }}
    
  />
</label>

          {/* CREATE BATCH CALL BUTTON */}
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <FaPlus /> Create Batch Call
          </button>
        </div>
      </div>
    {/* SYNC ALL BATCHES BUTTON */}
<button className="btn btn-secondary" onClick={handleSyncAll}>
  <FaSync /> Sync All
</button>

      {/* FILTERS */}
      <h4><FaFilter /> Filters</h4>
      <div className="filter-panel">
        <label>Status</label>
        <select
          className="form-control"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <button className="btn btn-secondary"
          onClick={() => setFilterStatus("all")}
        >
          Reset Filters
        </button>
      </div>

      {/* STATS */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon primary"><FaUsers /></div>
            <h3>{stats.total_batch_calls}</h3>
            <p>Total Batch Calls</p>
          </div>

          <div className="stat-card">
            <div className="stat-icon info"><FaPhone /></div>
            <h3>{stats.total_recipients}</h3>
            <p>Total Recipients</p>
          </div>

          <div className="stat-card">
            <div className="stat-icon success"><FaCheckCircle /></div>
            <h3>{stats.completed_calls}</h3>
            <p>Completed</p>
          </div>

          <div className="stat-card">
            <div className="stat-icon warning"><FaChartLine /></div>
            <h3>{stats.success_rate}%</h3>
            <p>Success Rate</p>
          </div>
        </div>
      )}

      {/* BATCH CALL LIST */}
      <div className="batch-calls-list">
        {batchCalls.length === 0 ? (
          <div className="empty-state">
            <FaPhone className="empty-icon" />
            <h3>No batch calls found</h3>
          </div>
        ) : (
          batchCalls.map((batch) => (
           <div key={batch.id} className="batch-call-card">
  <div className="batch-header">
    <div className="batch-info">
      <h3>{batch.name}</h3>
      <div className="batch-meta">
        <span><FaClock /> {formatDate(batch.created_at)}</span>
        <span><FaUsers /> {batch.recipients_count}</span>
      </div>
    </div>

    <div className="batch-actions">
      <span className={`status-badge badge-${batch.status}`}>
        {batch.status.toUpperCase()}
      </span>

      {/* ⭐ NEW SYNC BATCH BUTTON */}
      <button
        className="btn btn-xs btn-outline sync-btn"
        onClick={() => handleSyncBatch(batch.id)}
      >
        <FaSync /> Sync
      </button>
    </div>
  </div>


              <div className="batch-progress">
                <div className="progress-info">
                  <span>{batch.total_calls_dispatched} / {batch.total_calls_scheduled}</span>
                  <span>
                    {Math.round(
                      (batch.total_calls_dispatched / batch.total_calls_scheduled) *
                      100
                    )}%
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width:
                        (batch.total_calls_dispatched / batch.total_calls_scheduled) *
                        100 + "%",
                    }}
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>


{/* ========================================================= */}
{/* UPLOAD PREVIEW MODAL (ENHANCED UI)                        */}
{/* ========================================================= */}
{showUploadPreviewModal && (
  <div
    className="modal-overlay"
    onClick={() => setShowUploadPreviewModal(false)}
  >
    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
      <div className="modal-box-header">
        <h3>Uploaded Leads Preview</h3>
        <button
          className="modal-box-close"
          onClick={() => setShowUploadPreviewModal(false)}
        >
          ×
        </button>
      </div>

      <div className="modal-box-body modal-box-body-scroll">
        {/* Summary strip */}
        <div className="preview-summary">
          <div>
            <h4>New Leads Found</h4>
            <p>
              We extracted{" "}
              <strong>{uploadPreview?.created_total || 0}</strong> new lead
              {uploadPreview?.created_total === 1 ? "" : "s"} from your
              documents.
            </p>
          </div>
          <div className="preview-summary-chip">
            <FaUsers />
            <span>{uploadPreview?.created_total || 0} leads</span>
          </div>
        </div>

        {/* Uploaded leads list */}
        {uploadPreview?.created_leads?.length > 0 ? (
          <div className="preview-leads-grid">
            {uploadPreview.created_leads.map((lead, i) => (
              <div key={i} className="preview-lead-card">
                {/* Header row: index, name, phone */}
                <div className="preview-lead-header">
                  <div className="preview-lead-title">
                    <span className="lead-index-badge">#{i + 1}</span>
                    <h5>{lead.name || "Unnamed lead"}</h5>
                  </div>

                  <div className="preview-lead-phone">
                    <FaPhone />
                    <span>{lead.phone || "No phone number"}</span>
                  </div>
                </div>

                {/* Main body: meta + notes */}
                <div className="preview-lead-body">
                  {/* Left side meta */}
                  <div className="preview-lead-meta">
                    {/* <div className="meta-row">
                      <FaUser className="meta-icon" />
                      <div>
                        <div className="meta-label">Contact Person</div>
                        <div className="meta-value">
                          {lead.contact_person || "Unable to read contact person"}
                        </div>
                      </div>
                    </div> */}

                    <div className="meta-row">
                      <FaMapMarkerAlt className="meta-icon" />
                      <div>
                        <div className="meta-label">Address</div>
                        <div className="meta-value">
                          {lead.address || "—"}
                        </div>
                      </div>
                    </div>

                    <div className="meta-row">
                      <FaDollarSign className="meta-icon" />
                      <div>
                        <div className="meta-label">Proposed Price</div>
                        <div className="meta-value">
                          {lead.proposed_price || "Not specified"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side notes */}
                  <div className="preview-lead-notes">
                    <div className="notes-block">
                      <div className="notes-label">
                        <FaStickyNote /> Call Script
                      </div>
                      <p className="notes-text">
                        {lead.context || "No script extracted."}
                      </p>
                    </div>

                    <div className="notes-block">
                      <div className="notes-label">
                        <FaFileUpload /> Project Description
                      </div>
                      <p className="notes-text">
                        {lead.project_description || "No project description."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p>No new leads created.</p>
        )}

        {/* Failed docs */}
        <h4 className="mt-3">Failed Documents</h4>
        {uploadPreview?.failed_leads?.length > 0 ? (
          <div className="failed-docs-list">
            {uploadPreview.failed_leads.map((f, i) => (
              <div key={i} className="failed-item">
                <strong>{f.file_name}</strong> — {f.reason}
              </div>
            ))}
          </div>
        ) : (
          <p className="muted-text">No failed documents 🎉</p>
        )}
      </div>

      <div className="modal-box-footer">
        {uploadPreview?.created_leads?.length > 0 && (
          <button
            className="btn btn-primary"
            onClick={() => {
              // preselect just the newly created leads
              setSelectedLeads(uploadPreview.created_leads.map((l) => l.id));
              setShowUploadPreviewModal(false);
              setShowCreateModal(true);
            }}
          >
            <FaUsers /> Create Batch Call with These Leads
          </button>
        )}

        <button
          className="btn btn-secondary"
          onClick={() => setShowUploadPreviewModal(false)}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

{/* ========================================================= */}
{/* CREATE BATCH CALL MODAL (ENHANCED UI + PAGINATION)        */}
{/* ========================================================= */}
{showCreateModal && (
  <div
    className="modal-overlay"
    onClick={() => !creating && setShowCreateModal(false)}
  >
    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
      <div className="modal-box-header">
        <h3>Create Batch Call</h3>
        <button
          className="modal-box-close"
          onClick={() => setShowCreateModal(false)}
        >
          ×
        </button>
      </div>

      <div className="modal-box-body">
        {/* Top summary bar */}
        <div className="batch-create-summary">
          <div>
            <h4>Batch details</h4>
            <p className="summary-text">
              Name your campaign and pick an optional schedule time, then select
              which leads to include.
            </p>
          </div>
          <div className="batch-summary-pill">
            <FaUsers />
            <span>
              {selectedLeads.length} selected /{" "}
              {leadsPagination.count || leads.length} total leads
            </span>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="batch-create-grid">
          {/* LEFT: form fields */}
          <div className="batch-form-panel">
            <label className="modal-label">Batch Call Name *</label>
            <input
              className="modal-input"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              placeholder="e.g., Monday Follow-ups"
            />

 <label className="modal-label">Schedule Time (Optional)</label>

<div className="schedule-input-wrapper">
  <input
    type="datetime-local"
    className="modal-input schedule-input"
    value={scheduledTime}
    onChange={(e) => setScheduledTime(e.target.value)}

    // Prevent typing
    onKeyDown={(e) => e.preventDefault()}

    // Helps avoid mobile keyboard popping
    inputMode="none"
  />
</div>

            <div className="batch-hint-box">
              <FaClock className="hint-icon" />
              <div>
                <div className="hint-title">Scheduling tip</div>
                <div className="hint-text">
                  If you don’t set a time, the batch will be queued to start as
                  soon as possible.
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: leads selection */}
          <div className="batch-leads-panel">
            <div className="leads-select-header">
              <label className="modal-label">
                Leads ({selectedLeads.length} selected)
              </label>
              <button
                className="btn btn-secondary btn-sm"
                onClick={selectAllLeads}
                disabled={leadsLoading || leads.length === 0}
              >
                {selectedLeads.length === leads.length
                  ? "Deselect All"
                  : "Select All"}
              </button>
            </div>

            <div className="modal-leads-list">
              {leadsLoading && (
                <div className="leads-inline-loader">
                  <FaSpinner className="spin" /> Loading leads...
                </div>
              )}

              {!leadsLoading &&
                leads.map((lead) => {
                  const isSelected = selectedLeads.includes(lead.id);
                  return (
                    <div
                      key={lead.id}
                      className={`modal-lead-item ${
                        isSelected ? "selected" : ""
                      }`}
                      onClick={() => toggleLeadSelection(lead.id)}
                    >
                      <div className="modal-lead-main">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                        />

                        <div className="modal-lead-text">
                          {/* Top row: name + price chip */}
                          <div className="modal-lead-top-row">
                            <strong>{lead.name || "Unnamed lead"}</strong>

                            {lead.proposed_price && (
                              <span className="lead-chip lead-chip-price">
                                <FaDollarSign />
                                {lead.proposed_price}
                              </span>
                            )}
                          </div>

                          {/* Secondary info: phone + contact person */}
                          <div className="modal-lead-secondary">
                            <span className="lead-inline">
                              <FaPhone /> {lead.phone || "No phone"}
                            </span>

                            {lead.contact_person && (
                              <span className="lead-inline">
                                <FaUser /> {lead.contact_person}
                              </span>
                            )}
                          </div>

                          {/* Optional project description / context snippet */}
                          {lead.project_description && (
                            <p className="modal-lead-note">
                              {lead.project_description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

              {!leadsLoading && leads.length === 0 && (
                <div className="empty-leads-hint">
                  No leads available yet. Try uploading documents to generate
                  leads first.
                </div>
              )}
            </div>

            {/* Pagination bar */}
            <div className="pagination-bar">
              <button
                className="btn btn-secondary btn-sm"
                disabled={
                  leadsLoading ||
                  !leadsPagination.previous ||
                  leadsPage <= 1
                }
                onClick={() => changeLeadsPage(leadsPage - 1)}
              >
                Previous
              </button>

              <span className="pagination-info">
                Page {leadsPage} of{" "}
                {Math.max(
                  1,
                  Math.ceil(
                    (leadsPagination.count || 0) / LEADS_PAGE_SIZE
                  )
                )}
              </span>

              <button
                className="btn btn-secondary btn-sm"
                disabled={
                  leadsLoading || !leadsPagination.next
                }
                onClick={() => changeLeadsPage(leadsPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-box-footer">
        <button
          className="btn btn-secondary"
          onClick={() => setShowCreateModal(false)}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleCreateBatchCall}
          disabled={creating || selectedLeads.length === 0}
        >
          {creating ? <FaSpinner className="spin" /> : <FaPlay />}
          {creating ? " Creating..." : "Create Batch"}
        </button>
      </div>
    </div>
  </div>
)}




{uploading && (
  <div className="loader-overlay">
    <div className="loader-box">
      <FaSpinner className="spin loader-icon" />
      <h4>Processing uploaded documents…</h4>

      {uploadedFileNames.length > 0 && (
        <>
          <p className="loader-subtext">We’re extracting leads from:</p>
          <ul className="loader-file-list">
            {uploadedFileNames.map((name, idx) => (
              <li key={idx}>{name}</li>
            ))}
          </ul>
        </>
      )}

      <p className="loader-subtext">
        This may take a moment. Stay on this page.
      </p>
    </div>
  </div>
)}


  </div>
  );
};

export default BatchCalling;
